/**
 * WHICH PROMPT ANSWERS FOR THIS LEAVE — the one decision the visit note's
 * in-app dialog is allowed to make, extracted so it can be tested. The rest of
 * the wiring (holding the paused navigation, rendering `LeaveNotePrompt`) lives
 * in MemberProfilePage, which is not mountable in a unit test.
 *
 * Three write surfaces share MemberProfilePage's leave guards: Create Auth
 * (AM-5694), Create Claim (AM-5921) and the visit note (PR #362). Folding the
 * note into the existing guard is what put a browser alert in front of it —
 * "Are you sure you want to leave this page? Any unsaved changes will be lost."
 * — which is the wrong shape (the reviewing physician asked for an in-app
 * choice) and, since the note autosaves a draft, not even true.
 *
 * So the note gets the in-app dialog and the two native write forms keep the
 * native confirm. Deliberately, not by omission:
 *   - For Create Auth / Create Claim the browser alert is TRUE. Nothing persists
 *     a half-entered CMS-1500 anywhere, so "unsaved changes will be lost" is an
 *     accurate data-loss warning, and re-shaping it as a clinical prompt would
 *     be a second, unasked-for change to a shipped flow.
 *   - The note is the surface that regressed, and the only one with a draft
 *     behind it, so it is the only one whose honest message is "you haven't
 *     filed this yet" rather than "you will lose this".
 *
 * AND WHEN BOTH ARE DIRTY, THE NATIVE CONFIRM WINS. Real data loss outranks a
 * you-haven't-filed-it nudge, and stacking both prompts would ask twice for one
 * navigation. That is the `auth || claim` short-circuit below.
 */
export interface LeaveDirtyState {
  /** Create Auth form (AM-5694) — genuine unsaved data. */
  auth: boolean;
  /** Create Claim form (AM-5921) — genuine unsaved data. */
  claim: boolean;
  /** Visit note draft (PR #362) — autosaved, but not written to the chart. */
  note: boolean;
}

/**
 * True when the visit note's in-app dialog should answer this leave. False means
 * "not mine" — the caller falls through to whatever it did before, which for a
 * dirty auth/claim form is the native confirm and for a clean page is nothing
 * at all.
 */
export function noteDialogOwnsLeave({
  auth,
  claim,
  note,
}: LeaveDirtyState): boolean {
  if (auth || claim) return false;
  return note;
}

/**
 * What a leave REQUEST should do, given whether the dialog is already up — the
 * re-entrancy half of the decision, extracted for the same reason as
 * `noteDialogOwnsLeave`: MemberProfilePage is not mountable in a unit test.
 *
 * WHY THE MIDDLE CASE EXISTS. `window.confirm` blocked the event loop, so a
 * second navigation request could never interleave with an open question. The
 * in-app dialog opens that window: while `LeaveNotePrompt` is up, a second
 * browser Back press re-enters the popstate guard, which calls the host's
 * `requestNoteLeave` again. Unguarded, that second call silently REPLACED the
 * stored continuation — re-answering a question the user was still being asked
 * — and the user's eventual "Leave the note" ran a continuation belonging to a
 * navigation they never confirmed was the same one.
 *
 *  - `"not-ours"`      — the dialog does not own this leave
 *    (`noteDialogOwnsLeave` said no); the caller falls through to the native
 *    confirm exactly as before, open dialog or not.
 *  - `"already-asking"` — the dialog IS the answer and it is already on screen
 *    asking it. The caller reports the leave as handled (`true`) but must NOT
 *    store the new continuation: the FIRST question stands, and the user's
 *    single answer resolves it exactly once.
 *  - `"open-dialog"`   — the dialog owns it and is not up yet: store the
 *    continuation and render it.
 */
export type NoteLeaveRequestOutcome =
  | "not-ours"
  | "already-asking"
  | "open-dialog";

export function decideNoteLeaveRequest(
  dirty: LeaveDirtyState,
  dialogAlreadyOpen: boolean,
): NoteLeaveRequestOutcome {
  if (!noteDialogOwnsLeave(dirty)) return "not-ours";
  return dialogAlreadyOpen ? "already-asking" : "open-dialog";
}
